import {
  FileText,
  MagnifyingGlass,
  Pencil,
  Trash,
} from '@phosphor-icons/react';
import { useDebounce } from 'ahooks';
import { useEffect, useState } from 'react';
import { Button } from 'src/components/Button';
import { ContentWrapper } from 'src/components/ContentWrapper';
import { CopyableField } from 'src/components/CopyableField';
import { Heading } from 'src/components/Heading';
import { Input } from 'src/components/Input';
import { Modal } from 'src/components/Modal';
import { SideDropActions } from 'src/components/SideDropActions';
import Table from 'src/components/Table';
import { useEnvironment } from 'src/contexts/EnvironmentContext';
import { useNotification } from 'src/contexts/NotificationContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Can, can, Subject } from 'src/hooks/usePermissions';
import { QueryObject } from 'src/interfaces/queryObject.interface';
import { Webhook, WebhookPayload } from 'src/interfaces/webhook.interface';
import { limitString } from 'src/utils/limitString';
import { WebhookForm } from './components/WebhookForm';

type WebhookListItem = Webhook & {
  subscriptionsWithLabels: { label: string; value: string }[];
};

const subscriptionOptions = [
  { value: 'event_message', label: 'Message' },
  { value: 'event_action', label: 'Action' },
  { value: 'event_phone_call_connected', label: 'Phone Call Connected' },
  { value: 'event_phone_call_ended', label: 'Phone Call Ended' },
  {
    value: 'event_phone_call_did_not_connect',
    label: 'Phone Call Did Not Connect',
  },
  { value: 'event_transcript', label: 'Transcript' },
  {
    value: 'event_call_record_finalized',
    label: 'Post-Call Processing Completed',
  },
  { value: 'event_recording', label: 'Recording' },
  { value: 'event_human_detection', label: 'Human Detection' },
  { value: 'event_error', label: 'Error' },
];

const getLabelForSubscription = (subscription_key: string) => {
  const subscription = subscriptionOptions.find(
    (item) => item.value === subscription_key,
  );

  return subscription ? subscription.label : subscription_key;
};

const newWebhook: WebhookPayload = {
  label: '',
  subscriptions: [],
  url: '',
  method: 'POST',
  account_connection_id: '',
};

const headers = [
  { key: 'id', label: 'ID', width: '8rem' },
  { key: 'label', label: 'Label', width: '30%' },
  {
    key: 'subscriptions',
    label: 'Subscriptions',
    width: '30%',
    disableSorting: true,
  },
  { key: 'url', label: 'URL', width: '30%' },
  { key: 'method', label: 'Method', width: '10%', disableSorting: true },
  { key: 'actions', label: '', width: '20%', disableSorting: true },
];

export const ListWebhooks = () => {
  const { get, patch, post, remove } = useAuthedRequest();
  const [equivalentCodeOpen, setEquivalentCodeOpen] = useState(false);
  const notification = useNotification();

  const [rows, setRows] = useState<WebhookListItem[]>([]);
  const [filter, setFilter] = useState('');
  const debouncedFilter = useDebounce(filter, { wait: 500 });
  const [loading, setLoading] = useState(false);
  const [size, setSize] = useState(10);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalDeleteOpen, setModalDeleteOpen] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [form, setForm] = useState<WebhookPayload>(newWebhook);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [sortConfig, setSortConfig] = useState<{
    key: string | null;
    direction: string | null;
  }>({ key: null, direction: null });

  const { role } = useEnvironment();

  const fetchData = async (page: number, perPage: number) => {
    setLoading(true);
    setEquivalentCodeOpen(false);

    const queryObject: QueryObject = {
      page: String(page),
      size: String(perPage),
      filters: JSON.stringify({ url: filter, scope: 'global|user' }),
    };

    if (sortConfig.key) {
      queryObject.sort_column = sortConfig.key;
      queryObject.sort_desc =
        sortConfig.direction === 'desc' ? 'true' : 'false';
    }

    const query = new URLSearchParams(queryObject as Record<string, string>);

    const data = await get(`/webhooks?${query.toString()}`);

    const items = data.items;
    const webhooks: WebhookListItem[] = items.map((item: Webhook) => {
      const subscriptionsWithLabels = item.subscriptions.map(
        (subscription: string) => ({
          label: getLabelForSubscription(subscription),
          value: subscription,
        }),
      );
      return {
        ...item,
        subscriptionsWithLabels: subscriptionsWithLabels.sort((a, b) =>
          a.label.localeCompare(b.label),
        ),
      };
    });

    setRows(webhooks);
    setTotalItems(data.total);
    setLoading(false);
  };

  useEffect(() => {
    if (!loading) {
      fetchData(currentPage, size);
    }
  }, [currentPage, sortConfig, debouncedFilter, size]);

  const handlePageChange = (page: number, perPage: number) => {
    setCurrentPage(page);
    setSize(perPage);
  };

  const handleSizeChange = (perPage: number) => {
    setSize(perPage);
    setCurrentPage(1);
    fetchData(1, perPage);
  };

  const handleSort = (key: string, direction: string) => {
    setSortConfig({ key, direction });
  };

  const resetForm = () => {
    setForm(newWebhook);
  };

  const openModalNewWebhook = () => {
    setModalOpen(true);
    setSelectedId('');
    resetForm();
  };

  const openModalEditWebhook = (id: string) => {
    setModalOpen(true);
    setSelectedId(id);
    const webhook = rows.find((item) => item.id === id);

    if (webhook) {
      setForm({
        subscriptions: webhook.subscriptions,
        url: webhook.url,
        method: webhook.method,
        label: webhook.label,
        account_connection_id: webhook.account_connection_id,
      });
    }
  };

  const closeModal = () => {
    setEquivalentCodeOpen(false);
    setModalOpen(false);
    setSelectedId('');
    resetForm();
  };

  const submitModal = async () => {
    setLoading(true);

    if (selectedId) {
      await patch(`/webhooks/${selectedId}`, {
        ...form,
        label: form.label || 'Untitled Webhook',
      });
      notification.success('Webhook updated successfully');
    } else {
      await post('/webhooks', {
        ...form,
        label: form.label || 'Untitled Webhook',
      });
      notification.success('Webhook created successfully');
    }

    fetchData(currentPage, size);

    setLoading(false);

    closeModal();
  };

  useEffect(() => {
    if (equivalentCodeOpen && !loading) {
      setEquivalentCodeOpen(true);
    }
  }, [form, equivalentCodeOpen]);

  const isValidForm = () => {
    return form.subscriptions.length === 0 || form.url === '';
  };

  const disabledSubmit = () => {
    if (loading) return true;

    return isValidForm();
  };

  const openDeleteWebhook = (webhook: Webhook) => {
    setModalDeleteOpen(true);
    setSelectedId(webhook.id);
  };

  const handleRemoveWebhook = async () => {
    setLoading(true);
    try {
      await remove(`/webhooks/${selectedId}`);
      notification.success('Webhook deleted successfully');
      fetchData(currentPage, size);
      setModalDeleteOpen(false);
    } catch {
      notification.error('Failed to delete webhook');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex-1">
      <Heading
        title="Webhooks"
        subtitle="Webhooks allow you to listen to when specific events happen during a conversation"
      >
        <div className="flex">
          <Button
            className="w-42 border-none flex items-center justify-center text-neutral-500"
            variant="outlined"
            color="default"
            href="https://docs.fluents.ai/api-reference/webhooks"
            target="_blank"
          >
            View help doc
            <FileText className="ml-2" size={18} />
          </Button>
        </div>
      </Heading>

      <ContentWrapper>
        <div className="flex flex-col md:flex-row justify-between mb-6">
          <div className="flex w-full md:w-96 self-end">
            <Input
              type="text"
              required
              placeholder="Search webhooks"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              icon={<MagnifyingGlass size={20} />}
              iconPosition="left"
              inputAction={() => fetchData(currentPage, size)}
            />
          </div>

          <Can operation="create" subject={Subject.WEBHOOKS}>
            <Button className="mt-4 md:mt-0" onClick={openModalNewWebhook}>
              Create Webhook
            </Button>
          </Can>
        </div>

        <Can operation="read" subject={Subject.WEBHOOKS}>
          <Table
            headers={headers}
            onRowClick={(index: number) => {
              const webhook = rows[index] as unknown as Webhook;
              if (webhook && can(role, 'update', Subject.WEBHOOKS)) {
                openModalEditWebhook(webhook.id as string);
              }
            }}
            rows={
              rows.map((item: WebhookListItem) => ({
                ...item,
                id: (
                  <CopyableField value={item.id} notification={notification} />
                ),
                label: limitString(item?.label || '', 20),
                subscriptions: (
                  <div className="flex flex-wrap">
                    {item.subscriptionsWithLabels.map((subscription) => (
                      <div
                        key={subscription.value}
                        className="m-1 bg-primary p-1 text-neutral-0 rounded-md"
                      >
                        {subscription.label}
                      </div>
                    ))}
                  </div>
                ),
                actions: item.user_id && (
                  <div
                    className="flex justify-center items-center"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <SideDropActions
                      options={[
                        {
                          label: 'Edit',
                          Icon: Pencil,
                          onClick: () =>
                            openModalEditWebhook(item.id as string),
                          show: can(role, 'update', Subject.WEBHOOKS),
                        },
                        {
                          label: 'Delete',
                          Icon: Trash,
                          color: 'error',
                          onClick: () => openDeleteWebhook(item),
                          show: can(role, 'delete', Subject.WEBHOOKS),
                        },
                      ]}
                    />
                  </div>
                ),
              })) as never[]
            }
            totalItems={totalItems}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            onSort={handleSort}
            loading={loading}
            setSize={handleSizeChange}
            defaultSize={size}
          />
        </Can>
      </ContentWrapper>

      <Modal
        title={`${selectedId ? 'Edit' : 'New'} Webhook`}
        isOpen={modalOpen}
        onClose={() => closeModal()}
        actionButton={
          <Button onClick={submitModal} disabled={disabledSubmit()}>
            {selectedId ? 'Save Webhook' : 'Create Webhook'}
          </Button>
        }
      >
        <WebhookForm
          form={form}
          onChange={setForm}
          loading={loading}
          selectedId={selectedId}
        />
      </Modal>

      <Modal
        title="Delete Webhook"
        isOpen={modalDeleteOpen}
        onClose={() => {
          setModalDeleteOpen(false);
          setSelectedId('');
        }}
        actionButton={
          <Button onClick={handleRemoveWebhook}>Delete Webhook</Button>
        }
        className="w-96"
      >
        <div className="text-center">
          Are you sure you want to delete this webhook?
        </div>
      </Modal>
    </div>
  );
};
